/**
 * Service de configuration IA globale
 * Centralise tous les paramètres IA utilisés par l'application
 * Utilise l'API serveur pour la persistance
 */

import api from './api';

const DEFAULT_AI_CONFIG = {
  planningStrategy: 'multi-agents', // 🎯 Stratégie de planification par défaut
  provider: 'proxy-llm', // Fournisseur LLM pour le Manager IA
  model: 'openai/gpt-4o-mini', // Modèle LLM par défaut
  apiKey: '',
  temperature: 0.1,
  maxTokens: 4000,
  enableMultipleSolutions: false,
  maxSolutions: 3
};

class AIConfigService {
  constructor() {
    this.config = null;
    this.listeners = [];
  }

  /**
   * Charger la configuration IA depuis l'API serveur
   */
  async loadConfig() {
    try {
      console.log('📖 Chargement de la configuration IA depuis le serveur...');
      
      const response = await api.get('/api/admin/ai-config');
      
      console.log('🔍 DEBUG - Réponse complète du serveur:', response);
      
      if (response && response.success && response.config) {
        this.config = { ...DEFAULT_AI_CONFIG, ...response.config };
        console.log('✅ Configuration IA chargée depuis le serveur:', this.config);
      } else {
        console.warn('⚠️ Réponse serveur invalide, utilisation de la configuration par défaut');
        console.warn('🔍 DEBUG - response:', response);
        console.warn('🔍 DEBUG - response.success:', response?.success);
        console.warn('🔍 DEBUG - response.config:', response?.config);
        this.config = { ...DEFAULT_AI_CONFIG };
      }

      // Notifier les listeners
      this.notifyListeners();
      
      return this.config;
    } catch (error) {
      console.error('❌ Erreur lors du chargement de la configuration IA depuis le serveur:', error);
      
      // Fallback vers localStorage en cas d'erreur serveur
      try {
        const savedConfig = localStorage.getItem('ai-settings');
        if (savedConfig) {
          this.config = { ...DEFAULT_AI_CONFIG, ...JSON.parse(savedConfig) };
          console.log('📦 Configuration IA chargée depuis localStorage (fallback)');
        } else {
          this.config = { ...DEFAULT_AI_CONFIG };
        }
      } catch (fallbackError) {
        console.error('❌ Erreur fallback localStorage:', fallbackError);
        this.config = { ...DEFAULT_AI_CONFIG };
      }
      
      return this.config;
    }
  }

  /**
   * Sauvegarder la configuration IA sur le serveur
   */
  async saveConfig(newConfig) {
    try {
      console.log('💾 Sauvegarde de la configuration IA sur le serveur...', newConfig);
      
      const response = await api.post('/api/admin/ai-config', newConfig);
      
      if (response && response.success) {
        this.config = { ...DEFAULT_AI_CONFIG, ...response.config };
        console.log('✅ Configuration IA sauvegardée sur le serveur:', this.config);
        
        // Sauvegarder aussi en localStorage comme backup
        localStorage.setItem('ai-settings', JSON.stringify(this.config));
        
        // Notifier les listeners
        this.notifyListeners();
        
        return true;
      } else {
        throw new Error(response?.error || 'Erreur de sauvegarde sur le serveur');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la configuration IA sur le serveur:', error);
      
      // Fallback vers localStorage
      try {
        this.config = { ...this.config, ...newConfig };
        localStorage.setItem('ai-settings', JSON.stringify(this.config));
        console.log('📦 Configuration IA sauvegardée dans localStorage (fallback)');
        
        // Notifier les listeners
        this.notifyListeners();
        
        // Re-lancer l'erreur pour informer l'utilisateur
        throw new Error(`Erreur serveur: ${error.message}. Configuration sauvegardée localement.`);
      } catch (fallbackError) {
        console.error('❌ Erreur fallback localStorage:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Obtenir la configuration actuelle (synchrone)
   */
  getConfig() {
    if (!this.config) {
      // Charger de manière synchrone depuis localStorage comme fallback
      try {
        const savedConfig = localStorage.getItem('ai-settings');
        if (savedConfig) {
          this.config = { ...DEFAULT_AI_CONFIG, ...JSON.parse(savedConfig) };
          console.log('📦 Configuration IA chargée depuis localStorage (synchrone)');
        } else {
          this.config = { ...DEFAULT_AI_CONFIG };
          console.log('🔧 Configuration IA par défaut utilisée (synchrone)');
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement synchrone de la configuration IA:', error);
        this.config = { ...DEFAULT_AI_CONFIG };
      }
    }
    return this.config;
  }

  /**
   * Obtenir la configuration pour les requêtes API
   */
  getAPIConfig() {
    const config = this.getConfig();
    
    return {
      provider: config.provider,
      model: config.model,
      apiKey: config.apiKey,
      temperature: config.temperature,
      maxTokens: config.maxTokens,
      enableMultipleSolutions: config.enableMultipleSolutions,
      maxSolutions: config.maxSolutions
    };
  }

  /**
   * Obtenir les headers pour les requêtes API
   */
  getAPIHeaders() {
    const config = this.getConfig();
    const headers = {
      'Content-Type': 'application/json'
    };

    // Ajouter la clé API si nécessaire
    if (config.apiKey && config.provider !== 'proxy-llm') {
      headers['Authorization'] = `Bearer ${config.apiKey}`;
    }

    return headers;
  }

  /**
   * Obtenir l'URL de base pour les requêtes API
   */
  getAPIBaseURL() {
    const config = this.getConfig();
    
    switch (config.provider) {
      case 'proxy-llm':
        return process.env.REACT_APP_API_URL || 'http://localhost:3001';
      case 'openai':
        return 'https://api.openai.com/v1';
      case 'anthropic':
        return 'https://api.anthropic.com/v1';
      default:
        return process.env.REACT_APP_API_URL || 'http://localhost:3001';
    }
  }

  /**
   * Ajouter un listener pour les changements de configuration
   */
  addListener(callback) {
    this.listeners.push(callback);
    
    // Retourner une fonction pour supprimer le listener
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notifier tous les listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.config);
      } catch (error) {
        console.error('Erreur dans un listener de configuration IA:', error);
      }
    });
  }

  /**
   * Réinitialiser la configuration aux valeurs par défaut
   */
  resetToDefaults() {
    this.config = { ...DEFAULT_AI_CONFIG };
    localStorage.removeItem('ai-settings');
    this.notifyListeners();
  }

  /**
   * Valider la configuration
   */
  validateConfig(config) {
    const errors = [];

    if (!config.provider) {
      errors.push('Le fournisseur IA est requis');
    }

    if (!config.model) {
      errors.push('Le modèle IA est requis');
    }

    if (config.temperature < 0 || config.temperature > 2) {
      errors.push('La température doit être entre 0 et 2');
    }

    if (config.maxTokens < 100 || config.maxTokens > 32000) {
      errors.push('Le nombre de tokens doit être entre 100 et 32000');
    }

    if (config.enableMultipleSolutions && (config.maxSolutions < 2 || config.maxSolutions > 5)) {
      errors.push('Le nombre de solutions doit être entre 2 et 5');
    }

    return errors;
  }
}

// Instance singleton
const aiConfigService = new AIConfigService();

export default aiConfigService;
